import React from 'react';
import styled, { css } from 'styled-components';
import _ from 'lodash';
import { height, space, width, layout } from 'styled-system';

import ExitIcon from 'shared/assets/exit-icon';

export const ModalExitIcon = (props) => (
  <ModalExitIconStyles aria-label='Close' {...props} autoFocus>
    <ExitIcon />
  </ModalExitIconStyles>
);

const ModalExitIconStyles = styled.button`
  appearance: none;
  border: none;
  padding: 0;
  background-color: transparent;
  cursor: pointer;
  width: 27px;
  position: absolute;
  z-index: 10;
  top: 24px;
  right: 24px;
  fill: ${({ theme }) => theme?.colors?.grey[45]};
`;

const Modal = styled.div.attrs(({ top }) => ({
  tabIndex: '-1',
  style: {
    top,
  },
}))`
  position: relative;
  padding: 31px 47px 45px 47px;
  background-color: ${({ theme }) => theme?.colors?.white};
  border-radius: 28px;
  box-shadow: 0 5px 15px 0 rgba(0, 0, 0, 0.14);
  min-height: 78px;
  margin: 0 auto;
  text-align: center;
  top: 50%;
  transform: translateY(-50%);

  ${({ isIframe, additionalMobileCSS = '' }) =>
    isIframe &&
    css`
      position: absolute;
      left: 50%;
      transform: translate(-50%, -50%);

      ${({ theme }) => theme?.breakpoints?.down('sm')} {
        transform: translate(-50%, 0);
        ${additionalMobileCSS}
      }
    `}

  ${space}
  ${width}
  ${height}
  ${layout}

  ${({ theme }) => theme?.breakpoints?.down('xs')} {
    width: ${({ viewportWidth }) => (viewportWidth ? `${viewportWidth}px` : `calc(100vw - 16px)`)};
  }
`;

/**
 * @type {React.FC<
 * {
 *  ref?: any;
 *  children?: React.ReactNode;
 *  parentOffset?: number | string;
 *  viewportHeight?: number | string;
 *  viewportWidth?: number | string;
 *  iframeHeight?: number | string | null;
 *  isIframe?: boolean;
 *  additionalMobileCSS?: string;
 *  onRequestClose?: () => void;
 * }
 * & import("styled-system").SpaceProps
 * & import("styled-system").LayoutProps
 * >}
 */
export const ModalContainer = React.forwardRef(
  (
    {
      children,
      parentOffset = null,
      viewportHeight = null,
      viewportWidth = null,
      isIframe = false,
      iframeHeight = null,
      disableTopOffset = false,
      ...props
    },
    ref
  ) => {
    const lower = viewportHeight / 2;
    const upper = iframeHeight - lower;

    const desktopOffset = isIframe ? _.clamp(parentOffset + lower, lower, upper) : null;
    const mobileOffset = isIframe ? _.clamp(parentOffset, 0, upper) : null;

    const isMobile = viewportWidth <= 900;
    const top = isMobile ? mobileOffset : desktopOffset;

    return (
      <Modal ref={ref} top={disableTopOffset ? 0 : top} isIframe={isIframe} data-cy='modal-container' {...props}>
        {children}
      </Modal>
    );
  }
);

export const ModalFooter = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  height: 50px;
  width: 100%;
  background-color: ${({ theme }) => theme?.colors?.primaryGrey};
  border-radius: 0 0 28px 28px;
`;
